"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { EditorImageHoverButton } from "@/components/tailwind/ui/EditorImageHoverButton";
import { ImageLightbox } from "@/components/tailwind/ui/ImageLightbox";
import { setOpenImageLightbox, type LightboxImage } from "@/lib/image-lightbox-helper";

interface ImageLightboxContextValue {
  open: (images: LightboxImage[], index?: number) => void;
  close: () => void;
}

const ImageLightboxContext = createContext<ImageLightboxContextValue | undefined>(undefined);

export function ImageLightboxProvider({ children }: { children: ReactNode }) {
  const [images, setImages] = useState<LightboxImage[] | null>(null);
  const [index, setIndex] = useState(0);

  const open = useCallback((next: LightboxImage[], startAt = 0) => {
    const usable = next.filter((image) => Boolean(image?.src));
    if (!usable.length) return;
    setImages(usable);
    setIndex(Math.min(Math.max(startAt, 0), usable.length - 1));
  }, []);

  const close = useCallback(() => setImages(null), []);

  // Lets ProseMirror plugins and plain DOM listeners open the viewer too.
  useEffect(() => {
    setOpenImageLightbox(open);
    return () => setOpenImageLightbox(null);
  }, [open]);

  const value = useMemo(() => ({ open, close }), [open, close]);

  return (
    <ImageLightboxContext.Provider value={value}>
      {children}
      <EditorImageHoverButton />
      {images && (
        <ImageLightbox images={images} index={index} onIndexChange={setIndex} onClose={close} />
      )}
    </ImageLightboxContext.Provider>
  );
}

export function useImageLightbox() {
  const context = useContext(ImageLightboxContext);
  if (!context) throw new Error("useImageLightbox must be used within ImageLightboxProvider");
  return context;
}
